'use no memo';
import type {PointerEvent, ReactNode} from 'react';
import {useEffect} from 'react';
import {useRef} from 'react';
import {useState} from 'react';
import React from 'react';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import type {FabricObject} from '@postermywall/fabricjs-2';
import {useAppSelector} from '@/hooks';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {CUSTOM_ITEM_SCALING_CONTROL_SIDE, CustomControlCursor} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {CustomCornerControl, onScaleFromCornerEnded, scaleFromCorner} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {CUSTOM_TL_CONTROL_ID} from './custom-item-control-tl.types';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';

export function CustomItemControlTl(props: CustomItemControlsProps): ReactNode | null {
  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });
  const [isActive, setIsActive] = useState(false);
  const initialClientX = useRef<number | null>(null);
  const initialClientY = useRef<number | null>(null);

  const handlePointerDown = (e: PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    const target = e.target as HTMLElement;
    if (target.id === CUSTOM_TL_CONTROL_ID) {
      setIsActive(true);
      initialClientX.current = e.clientX;
      initialClientY.current = e.clientY;

      page.setPosterItemsModificationState('scaling');
    }
  };

  const getTlControlPosition = (): CustomItemControlPositions => {
    const leftInitial = props.positionProperties.left * props.positionProperties.posterScale;
    const topInitial = props.positionProperties.top * props.positionProperties.posterScale;

    const angle = props.positionProperties.angle;
    const left = leftInitial - CUSTOM_ITEM_SCALING_CONTROL_SIDE / 2;
    const top = topInitial - CUSTOM_ITEM_SCALING_CONTROL_SIDE / 2;

    return {left, top, angle};
  };

  const getCursor = (fabricObject: FabricObject): CustomControlCursor => {
    let angle = fabricObject.getTotalAngle();
    if (angle < 0) {
      angle = angle + 360;
    }

    if (angle >= 0 && angle < 23) {
      return CustomControlCursor.CURSOR_NWSE_RESIZE;
    }
    if (angle >= 23 && angle < 68) {
      return CustomControlCursor.CURSOR_NS_RESIZE;
    }
    if (angle >= 68 && angle < 113) {
      return CustomControlCursor.CURSOR_NESW_RESIZE;
    }
    if (angle >= 113 && angle < 158) {
      return CustomControlCursor.CURSOR_EW_RESIZE;
    }
    if (angle >= 158 && angle < 203) {
      return CustomControlCursor.CURSOR_NWSE_RESIZE;
    }
    if (angle >= 203 && angle < 248) {
      return CustomControlCursor.CURSOR_NS_RESIZE;
    }
    if (angle >= 248 && angle < 293) {
      return CustomControlCursor.CURSOR_NESW_RESIZE;
    }
    if (angle >= 293 && angle < 338) {
      return CustomControlCursor.CURSOR_EW_RESIZE;
    }
    return CustomControlCursor.CURSOR_NWSE_RESIZE;
  };

  const getTlControl = (): ReactNode | null => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return null;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return null;
    }

    if (activeSelection.lockScalingX || activeSelection.lockScalingY) {
      return null;
    }

    const position = getTlControlPosition();

    return (
      <BaseCustomItemControl
        controlId={CUSTOM_TL_CONTROL_ID}
        top={position.top}
        left={position.left}
        width={CUSTOM_ITEM_SCALING_CONTROL_SIDE}
        height={CUSTOM_ITEM_SCALING_CONTROL_SIDE}
        angle={position.angle}
        borderRadius={'4px'}
        isVisible={(!arePosterItemsMoving && !arePosterItemsRotating && !arePosterItemsScaling) || (arePosterItemsScaling && isActive)}
        cursor={getCursor(activeSelection)}
        onPointerDown={handlePointerDown}
      />
    );
  };

  const onItemScaledWithTopLeftHandle = (e: globalThis.PointerEvent): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    const activeSelection = page.activeSelection.getActiveObject();
    if (!activeSelection) {
      return;
    }

    if (initialClientX.current === null || initialClientY.current === null) {
      return;
    }

    const didScale = scaleFromCorner(e, initialClientX.current, initialClientY.current, page, activeSelection, CustomCornerControl.TL);
    if (didScale) {
      updateInitialRefs(e);
    }
  };

  const updateInitialRefs = (e: globalThis.PointerEvent): void => {
    initialClientX.current = e.clientX;
    initialClientY.current = e.clientY;
  };

  const handlePointerMove = (e: globalThis.PointerEvent): void => {
    if (!isActive) {
      return;
    }

    onItemScaledWithTopLeftHandle(e);
  };

  const handlePointerUp = (): void => {
    if (!isActive) {
      return;
    }

    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page) {
      return;
    }

    void onScaleFromCornerEnded();

    setIsActive(false);

    page.setPosterItemsModificationState(false);

    initialClientX.current = null;
    initialClientY.current = null;
  };

  useEffect(() => {
    if (isActive) {
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointermove', handlePointerMove);
    }

    return (): void => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isActive]);

  if (arePosterItemsMoving || arePosterItemsRotating || (arePosterItemsScaling && !isActive)) {
    return null;
  }

  return getTlControl();
}
